import { apiService } from './api'
import { 
  LoginCredentials, 
  RegisterData, 
  AuthResponse, 
  RefreshTokenResponse,
  PasswordResetRequest,
  PasswordReset,
  ChangePassword,
  User
} from '@/types/auth'

class AuthService {
  private loginAttempts = 0
  private lockoutUntil: Date | null = null

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    // Check if user is locked out
    if (this.lockoutUntil && new Date() < this.lockoutUntil) {
      const remainingTime = Math.ceil((this.lockoutUntil.getTime() - new Date().getTime()) / 60000)
      return { 
        success: false, 
        message: `Account locked. Try again in ${remainingTime} minutes.` 
      }
    }

    try {
      const response = await apiService.post<AuthResponse>('/auth/login', credentials)
      
      if (response.success) {
        // Reset login attempts on success
        this.loginAttempts = 0
        this.lockoutUntil = null
        return response
      } else {
        // Increment login attempts
        this.loginAttempts++
        
        // Check if should lock out
        const maxAttempts = parseInt(import.meta.env.VITE_MAX_LOGIN_ATTEMPTS || '5')
        if (this.loginAttempts >= maxAttempts) {
          const lockoutMinutes = parseInt(import.meta.env.VITE_LOGIN_LOCKOUT_MINUTES || '15')
          this.lockoutUntil = new Date(Date.now() + lockoutMinutes * 60000)
          return { 
            success: false, 
            message: `Too many failed attempts. Account locked for ${lockoutMinutes} minutes.` 
          }
        }
        
        return response
      }
    } catch (error) {
      return { 
        success: false, 
        message: 'Network error. Please try again.' 
      }
    }
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    return apiService.post('/auth/register', data)
  }

  async logout(): Promise<void> {
    try {
      await apiService.post('/auth/logout')
    } catch (error) {
      // Even if logout fails on server, clear local state
      console.error('Logout error:', error)
    }
  }

  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    return apiService.post('/auth/refresh', { refreshToken })
  }

  async forgotPassword(data: PasswordResetRequest): Promise<{ message: string }> {
    return apiService.post('/auth/forgot-password', data)
  }

  async resetPassword(data: PasswordReset): Promise<{ message: string }> {
    return apiService.post('/auth/reset-password', data)
  }

  async changePassword(data: ChangePassword): Promise<{ message: string }> {
    return apiService.put('/auth/change-password', data)
  }

  async verifyEmail(token: string): Promise<{ message: string }> {
    return apiService.post('/auth/verify-email', { token })
  }

  async resendVerification(email: string): Promise<{ message: string }> {
    return apiService.post('/auth/resend-verification', { email })
  }

  async getProfile(): Promise<{ success: boolean; data: User }> {
    const response = await apiService.get<{ success: boolean; data: User }>('/auth/profile')
    return response
  }

  async updateProfile(data: Partial<User>): Promise<{ success: boolean; data: User; message?: string }> {
    const response = await apiService.patch<{ success: boolean; data: User; message?: string }>('/auth/profile', data)
    return response
  }

  async uploadAvatar(file: File): Promise<{ success: boolean; data: { avatar: string }; message?: string }> {
    const response = await apiService.upload<{ success: boolean; data: { avatar: string }; message?: string }>('/auth/avatar', file)
    return response
  }

  async deleteAccount(): Promise<{ message: string }> {
    return apiService.delete('/auth/account')
  }
}

export const authService = new AuthService()
export default authService

